import { useApolloClient } from '@apollo/client'
import {
  EmployeeJobCatalogRolesFilterDocument,
  EmployeeJobCatalogTreeNodesFilterDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { SerializedTreeNode } from 'modules/job_catalog/pages/JobCatalogTreePage/types'

type Params = {
  hasJobCatalogTreeFeature: boolean
}

const flattenTreeToOptions = (
  nodes: SerializedTreeNode[]
): Array<{ label: string; value: string }> => {
  const options: Array<{ label: string; value: string }> = []

  const findRoles = (node: SerializedTreeNode): void => {
    if (node.type === 'jobcatalog_treerole' && node.children) {
      node.children.forEach((child) => {
        if (child.type === 'jobcatalog_treelevel' && child.name) {
          const combinedLabel = `${node.name} / ${child.name}`
          options.push({
            label: combinedLabel,
            value: child.node_uuid,
          })
        }
      })

      return
    }

    if (node.children) {
      node.children.forEach((child) => findRoles(child))
    }
  }

  nodes.forEach((node) => findRoles(node))

  return options
}

export const useJobCatalogRoleFilter = ({ hasJobCatalogTreeFeature }: Params) => {
  const client = useApolloClient()

  return {
    type: 'in' as const,
    label: i18n.t('employees.employees_list.filters.job_role'),
    options: {
      options: async () => {
        if (hasJobCatalogTreeFeature) {
          const { data } = await client.query({
            query: EmployeeJobCatalogTreeNodesFilterDocument,
            fetchPolicy: 'cache-first',
          })
          const tree: SerializedTreeNode[] = (() => {
            const parsedTree = data.jobCatalog.treesConnection.nodes[0]?.serializedTree
              ? JSON.parse(data.jobCatalog.treesConnection.nodes[0]?.serializedTree)
              : []

            return Array.isArray(parsedTree) ? parsedTree : [parsedTree]
          })()

          return flattenTreeToOptions(tree)
        }

        const { data } = await client.query({
          query: EmployeeJobCatalogRolesFilterDocument,
          fetchPolicy: 'cache-first',
        })

        return data.jobCatalog.roles.flatMap((role) =>
          role.levels.map((level) => ({
            label: level.isDefault ? role.name : `${role.name} / ${level.name}`,
            value: level.id,
          }))
        )
      },
    },
  }
}
